import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "react-toastify";
import { FaShareAlt, FaUserCircle } from "react-icons/fa";
import {
  deleteModeComment,
  getModeComments,
  getModeDetail,
  hideModeComment,
  postModeComment,
} from "../../api/community";
import PublishedModeCard from "../cards/PublishedModeCard";
import { Badge, Button, Card, Spinner, Textarea } from "../../ui";

const MAX_COMMENT = 1000;
const pct = (rate) => `${Math.round(rate * 100)}%`;

// What /api/community adds to a mode page for visitors: real stats, share,
// similar modes and the public comment thread. Renders nothing if the server
// doesn't have that API or the request fails — the page works without it.
const CommunityExtras = ({ modeId }) => {
  const { data: detail } = useQuery({
    queryKey: ["community", "detail", modeId],
    queryFn: () => getModeDetail(modeId),
    staleTime: 60 * 1000,
    retry: false,
  });
  if (!detail) return null;

  const stats = detail.stats || {};
  const tiles = [
    { label: "لاعب", value: stats.uniquePlayersAll ?? 0 },
    { label: "لعبة هذا الشهر", value: stats.plays?.d30 ?? 0 },
    { label: "في المفضلة", value: detail.mode?.favoritesCount ?? 0 },
  ];
  // Rates arrive only once there are enough plays behind them.
  if (typeof stats.answerRate === "number") {
    tiles.push({ label: "إجابات صحيحة", value: pct(stats.answerRate) });
  }
  if (typeof stats.completionRate === "number") {
    tiles.push({ label: "يكملون اللعبة", value: pct(stats.completionRate) });
  }

  const share = async () => {
    const url = detail.share?.url;
    if (!url) return;
    try {
      if (navigator.share) {
        await navigator.share({ title: detail.share.title, url });
      } else {
        await navigator.clipboard.writeText(url);
        toast.success("تم نسخ الرابط");
      }
    } catch (_) {
      // The share sheet was dismissed.
    }
  };

  return (
    <div className="space-y-8 mt-8">
      <div className="flex items-center justify-between gap-3">
        <div className="flex flex-wrap gap-2">
          {detail.category && (
            <Badge tone="muted">
              {detail.category.label?.ar}
            </Badge>
          )}
        </div>
        <Button variant="secondary" size="sm" onClick={share} icon={<FaShareAlt />}>
          مشاركة
        </Button>
      </div>

      <div className="grid grid-cols-3 gap-2 sm:gap-3">
        {tiles.map((t) => (
          <div
            key={t.label}
            className="rounded-2xl border border-white/10 bg-white/[0.05] p-3 text-center"
          >
            <p className="text-white text-lg md:text-xl font-extrabold">{t.value}</p>
            <p className="text-white/55 text-xs mt-0.5">{t.label}</p>
          </div>
        ))}
      </div>

      <ModeRow title="فئات مشابهة" modes={detail.similar} />
      <ModeRow title="المزيد من نفس الصانع" modes={detail.moreFromOwner} />

      {detail.commentsEnabled && (
        <Comments
          modeId={modeId}
          total={detail.commentsPreview?.total || 0}
        />
      )}
    </div>
  );
};

const ModeRow = ({ title, modes }) => {
  if (!modes?.length) return null;
  return (
    <section>
      <h2 className="text-lg font-bold mb-3">{title}</h2>
      <div className="flex gap-3 overflow-x-auto pb-2 snap-x">
        {modes.map((m) => (
          <div key={m._id} className="snap-start shrink-0 w-44">
            <PublishedModeCard mode={m} />
          </div>
        ))}
      </div>
    </section>
  );
};

const Comments = ({ modeId, total }) => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [text, setText] = useState("");
  const key = ["community", "comments", modeId];

  const thread = useInfiniteQuery({
    queryKey: key,
    queryFn: ({ pageParam }) => getModeComments(modeId, pageParam),
    initialPageParam: undefined,
    getNextPageParam: (last) => (last?.hasMore ? last.nextCursor : undefined),
    staleTime: 30 * 1000,
  });
  const comments = useMemo(
    () => (thread.data?.pages || []).flatMap((p) => p.comments || []),
    [thread.data],
  );
  const count = thread.data?.pages?.[0]?.total ?? total;
  const refresh = () => queryClient.invalidateQueries({ queryKey: key });

  const post = useMutation({
    mutationFn: () => postModeComment(modeId, text.trim()),
    onSuccess: () => {
      setText("");
      refresh();
    },
    onError: (e) => {
      const status = e?.response?.status;
      toast.error(
        status === 429
          ? "كتبت تعليقات كثيرة، انتظر قليلاً"
          : status === 409
            ? "أرسلت هذا التعليق للتو"
            : status === 403
              ? "لا يمكنك التعليق هنا"
              : "تعذّر إرسال التعليق",
      );
    },
  });
  const remove = useMutation({
    mutationFn: (id) => deleteModeComment(modeId, id),
    onSuccess: refresh,
    onError: () => toast.error("تعذّر حذف التعليق"),
  });
  const hide = useMutation({
    mutationFn: (id) => hideModeComment(modeId, id),
    onSuccess: refresh,
    onError: () => toast.error("تعذّر إخفاء التعليق"),
  });

  return (
    <Card as="section">
      <h2 className="text-lg font-bold mb-3">التعليقات ({count})</h2>

      <div className="mb-4">
        <Textarea
          value={text}
          onChange={(e) => setText(e.target.value)}
          maxLength={MAX_COMMENT}
          rows={3}
          dir="auto"
          placeholder="شاركنا رأيك في هذه الفئة…"
          aria-label="تعليقك"
          className="resize-none"
        />
        <div className="flex items-center justify-between mt-2">
          <span className="text-white/55 text-xs">
            {text.length}/{MAX_COMMENT}
          </span>
          <Button
            size="sm"
            onClick={() => post.mutate()}
            disabled={!text.trim() || post.isPending}
            icon={post.isPending ? <Spinner /> : null}
          >
            إرسال
          </Button>
        </div>
      </div>

      {thread.isLoading ? (
        <div className="flex justify-center py-6 text-white/80">
          <Spinner size={24} />
        </div>
      ) : thread.isError ? (
        <button
          onClick={() => thread.refetch()}
          className="text-primary text-sm font-semibold w-full py-4 hover:text-mainText"
        >
          تعذّر تحميل التعليقات · إعادة المحاولة
        </button>
      ) : comments.length === 0 ? (
        <p className="text-white/55 text-center text-sm py-4">
          لا توجد تعليقات بعد. كن أول من يعلّق!
        </p>
      ) : (
        <ul className="divide-y divide-white/10 border-t border-white/10">
          {comments.map((c) => (
            <li key={c._id} className="py-3 flex gap-3">
              <button
                onClick={() => c.user?._id && navigate(`/app/users/${c.user._id}`)}
                className="shrink-0"
                aria-label={c.user?.gameName || c.user?.name || "مستخدم"}
              >
                {c.user?.image ? (
                  <img src={c.user.image} alt="" className="w-9 h-9 rounded-full object-cover" />
                ) : (
                  <FaUserCircle className="w-9 h-9 text-white/30" />
                )}
              </button>
              <div className="flex-1 min-w-0">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="text-white text-sm font-semibold">
                    {c.user?.gameName || c.user?.name || "مستخدم"}
                  </span>
                  {c.isByModeOwner && (
                    <Badge tone="gold">
                      صاحب الفئة
                    </Badge>
                  )}
                  <span className="text-white/55 text-xs">
                    {new Date(c.createdAt).toLocaleDateString("ar", { numberingSystem: "latn" })}
                    {c.edited ? " · معدّل" : ""}
                  </span>
                </div>
                <p className="text-white/85 text-sm whitespace-pre-wrap break-words mt-1" dir="auto">
                  {c.content}
                </p>
                <div className="flex gap-4 mt-1">
                  {c.can?.delete && (
                    <button
                      onClick={() => remove.mutate(c._id)}
                      className="text-white/55 hover:text-danger text-xs font-semibold"
                    >
                      حذف
                    </button>
                  )}
                  {c.can?.hide && (
                    <button
                      onClick={() => hide.mutate(c._id)}
                      className="text-white/55 hover:text-white text-xs font-semibold"
                    >
                      إخفاء
                    </button>
                  )}
                </div>
              </div>
            </li>
          ))}
        </ul>
      )}

      {thread.hasNextPage && (
        <div className="flex justify-center mt-3">
          <button
            onClick={() => thread.fetchNextPage()}
            disabled={thread.isFetchingNextPage}
            className="text-primary text-sm font-bold flex items-center gap-2 hover:text-mainText disabled:opacity-60"
          >
            {thread.isFetchingNextPage && <Spinner />}
            عرض المزيد
          </button>
        </div>
      )}
    </Card>
  );
};

export default CommunityExtras;
